import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    Output,
    Renderer2,
} from '@angular/core';

@Component({
    selector: 'ham-modal',
    template: `<div class="ham-modal">
        <div class="ham-modal-inner" [ngClass]="size">
            <div class="modal-header" [ngClass]="headerClass" *ngIf="!hideHeader">
                <div *ngIf="!hideHeader" class="title-container">
                    <span *ngIf="titleIcon" class="ham-icon">
                        <i class="{{ titleIcon }}"></i>
                    </span>
                    <span>{{ title }}</span>
                </div>

                <span *ngIf="!hideCloseIcon && (!showCancelButton || showCloseIcon)" class="icon" (click)="onCancel()">
                    <i class="close-icon"></i>
                </span>
            </div>
            <div class="modal-body" [ngClass]="bodyClass">
                <ng-content>
                    <!-- This is where the modal content goes -->
                </ng-content>
            </div>
            <div *ngIf="!hideFooter" class="modal-footer" [ngStyle]="{ 'justify-content': footerAlignment }" [style.border-top]=" addHorizontalLineBeforeButtons ? '1px solid var(--gray-200, #E5E7EB)' : 'none' ">
                <div class="progress-indicator" *ngIf="progressTotal !== null && progressCurrent !== null">
                    {{ progressCurrent }} of {{ progressTotal }}
                </div>
                <div class="action-buttons" [ngStyle]="{ 'justify-content': footerAlignment }">
                    <ham-button *ngIf="showCancelButton && variant !== 'grayfooter'" outline="true" (click)="onCancelButtonClick()">{{
                        cancelText
                    }}</ham-button>
                    <ham-button
                        *ngIf="showCancelButton && variant === 'grayfooter'"
                        outline="true"
                        variant="transparent-gray"
                        (click)="onCancelButtonClick()"
                        >{{ cancelText }}</ham-button
                    >
                    <ham-button *ngIf="saveText" [variant]="saveVariant" [icon]="saveIcon" (click)="onSave()" [disabled]="disabled">{{
                        saveText
                    }}</ham-button>
                </div>
            </div>
        </div>
    </div> `,
    styleUrls: ['./modal.component.scss'],
})
export class HamModalComponent implements AfterViewInit, OnDestroy {
    @Output() cancel = new EventEmitter<void>(); // Event emitter for cancel action
    @Output() cancelButtonExclusivelyClicked = new EventEmitter<void>(); // New event emitter for cancel button clicks that are not close icon clicks
    @Output() save = new EventEmitter<void>(); // Event emitter for save action
    @Input() title: string = '';
    @Input() titleIcon: string | undefined;
    @Input() saveText: string;
    @Input() hideFooter = false;
    @Input() hideHeader = false;
    @Input() showCancelButton: boolean = false;
    @Input() showCloseIcon: boolean = false;
    @Input() hideCloseIcon: boolean = false; // Wins over the rule above, e.g. for a state that cannot be closed
    @Input() cancelText: string = '';
    @Input() headerClass: string = '';
    @Input() bodyClass: string = '';
    @Input() saveIcon: string | null = null;
    @Input() saveVariant: 'primary' | 'gray' | 'red' | 'teal' | 'green' | 'white' | 'transparent' | 'glass' | 'plain' =
        'primary';
    @Input() size: 'xs' | 'sm' | 'default' | 'lg' | 'xl' | 'xxl' = 'default';
    @Input() variant: 'grayfooter' | 'default' = 'default';
    @Input() footerAlignment: 'flex-start' | 'center' | 'flex-end' = 'flex-end';
    @Input() disabled: boolean = false;
    @Input() enableKeyboardShortcuts: boolean = true;
    @Input() addHorizontalLineBeforeButtons: boolean = false;
    @Input() progressTotal: number | null = null;
    @Input() progressCurrent: number | null = null;
    @Input() isCancelButtonDifferentFromCloseIcon: boolean = false; // New input to differentiate cancel button from close icon

    @HostBinding('class') get hostClasses(): string {
        return `ham-modal ${this.variant}`;
    }

    // Enter -> Save, Escape -> Cancel
    // Can be disabled via enableKeyboardShortcuts input
    @HostListener('document:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        if (!this.enableKeyboardShortcuts) {
            return;
        }
        if (event.key === 'Enter' && this.saveText && !this.disabled) {
            event.preventDefault();
            this.onSave();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            this.onCancel();
        }
    }

    onSave() {
        if (this.disabled) {
            return;
        }
        this.save.emit(); // Emit an event when the save button is clicked
    }

    onCancel() {
        this.cancel.emit(); // Emit the cancel event for both cancel button and close icon clicks
    }

    onCancelButtonClick() {

        if (this.isCancelButtonDifferentFromCloseIcon) {
            this.cancelButtonExclusivelyClicked.emit(); // Emit a separate event for cancel button clicks that are not close icon clicks
        } else {
            this.cancel.emit(); // Emit the cancel event for both cancel button and close icon clicks
        }

    }

    private element: HTMLElement;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2, // Inject Renderer2
    ) { }

    ngAfterViewInit(): void {
        this.element = this.el.nativeElement;

        // Move modal to the global placeholder
        this.moveModalToGlobalPlaceholder();
    }

    private moveModalToGlobalPlaceholder() {
        const placeholder = this.renderer.selectRootElement('#global-modal-placeholder', true);
        if (placeholder) {
            // Use Renderer2 to append the modal element to the global placeholder
            this.renderer.appendChild(placeholder, this.element);
        }
    }

    ngOnDestroy() {
        // Clean up the DOM using Renderer2
        if (this.element && this.element.parentElement) {
            this.renderer.removeChild(this.element.parentElement, this.element);
        }
    }
}
